import React, { ReactNode } from "react";
import {
  VictoryBar,
  VictoryChart,
  VictoryAxis,
  VictoryGroup,
  VictoryTooltip,
} from "victory";
import theme from "../../Styles/theme.scss";

const referralsData = [
  { x: "Oct '25", referrals: 18, installs: 12, unsuccessful: 6 },
  { x: "Nov '25", referrals: 24, installs: 18, unsuccessful: 6 },
  { x: "Dec '25", referrals: 22, installs: 15, unsuccessful: 7 },
  { x: "Jan '26", referrals: 30, installs: 22, unsuccessful: 8 },
  { x: "Feb '26", referrals: 28, installs: 20, unsuccessful: 8 },
  { x: "Mar '26", referrals: 35, installs: 26, unsuccessful: 9 },
];

const cumulativeData = [
  { x: "Jan '26", referrals: 180, installs: 155 },
  { x: "Feb '26", referrals: 230, installs: 195 },
  { x: "Mar '26", referrals: 284, installs: 225 },
];

const chartAxisStyle = {
  tickLabels: {
    fontSize: 8,
    fontFamily: "'DM Sans', sans-serif",
    fill: theme.neutral4,
  },
  grid: { stroke: theme.neutral3, strokeDasharray: "4,4" },
  axis: { stroke: theme.neutral3 },
  ticks: { stroke: "transparent" },
};

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
}

function StatCard({ label, value, sub }: StatCardProps) {
  return (
    <div
      style={{
        background: theme.white,
        border: `1.5px solid ${theme.neutral3}`,
        borderRadius: 16,
        padding: "24px 28px",
        flex: 1,
        minWidth: 0,
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: theme.neutral4,
          fontFamily: "'DM Sans', sans-serif",
          letterSpacing: 0.2,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "8px 0 0",
          fontSize: 40,
          fontWeight: 700,
          fontFamily: "'DM Serif Display', serif",
          color: theme.neutral7,
          lineHeight: 1.1,
        }}
      >
        {value}
      </p>
      {sub && (
        <p
          style={{
            margin: "8px 0 0",
            fontSize: 12,
            color: theme.neutral4,
            fontFamily: "'DM Sans', sans-serif",
            lineHeight: 1.4,
          }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

interface SectionLabelProps {
  number: number | string;
  children: ReactNode;
}

function SectionLabel({ number, children }: SectionLabelProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 16,
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          border: `2px solid ${theme.neutral6}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 700,
          fontFamily: "'DM Sans', sans-serif",
          flexShrink: 0,
          color: theme.neutral6,
        }}
      >
        {number}
      </span>
      <h2
        style={{
          margin: 0,
          fontSize: 17,
          fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          color: theme.neutral7,
        }}
      >
        {children}
      </h2>
    </div>
  );
}

interface ChartCardProps {
  title: string;
  children: ReactNode;
}

function ChartCard({ title, children }: ChartCardProps) {
  return (
    <div
      style={{
        background: theme.white,
        border: `1.5px solid ${theme.neutral3}`,
        borderRadius: 16,
        padding: "20px 16px 8px",
        flex: 1,
        minWidth: 0,
      }}
    >
      <p
        style={{
          margin: "0 0 4px",
          fontSize: 12,
          fontWeight: 600,
          color: theme.neutral4,
          fontFamily: "'DM Sans', sans-serif",
          textTransform: "uppercase",
          letterSpacing: 0.8,
        }}
      >
        {title}
      </p>
      {children}
    </div>
  );
}

export default function AccountPerformance() {
  const lastRefresh = "18:55, Friday, 13 March 2026";

  return (
    <div
      style={{
        background: theme.neutral2,
        minHeight: "100vh",
        fontFamily: "'DM Sans', sans-serif",
        padding: "0 0 60px",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: theme.white,
          borderBottom: `1.5px solid ${theme.neutral3}`,
          padding: "20px 24px 16px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 700,
            fontFamily: "'DM Serif Display', serif",
            color: theme.neutral7,
          }}
        >
          Account performance
        </h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 6,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12, color: theme.neutral4 }}>
            Data generated at {lastRefresh}
          </span>
          <button
            style={{
              fontSize: 11,
              padding: "4px 12px",
              borderRadius: 20,
              border: `1.5px solid ${theme.neutral3}`,
              background: "transparent",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              color: theme.neutral6,
            }}
          >
            Tap to refresh
          </button>
        </div>
      </div>

      <div style={{ padding: "28px 20px", maxWidth: 900, margin: "0 auto" }}>
        {/* Section 1: Current Snapshot */}
        <SectionLabel number="1">Current snapshot</SectionLabel>

        <div
          style={{
            display: "flex",
            gap: 14,
            marginBottom: 28,
            flexWrap: "wrap",
          }}
        >
          <StatCard
            label="Kits currently installed"
            value="82/100"
            sub="Against 100 contracted kits, leaving 18 available for deployment — 82% utilisation rate."
          />
          <StatCard
            label="Kits available for deployment"
            value="12"
            sub="12 kits are not being used and are available to install in new homes."
          />
        </div>

        {/* Kit utilisation bar */}
        <div
          style={{
            background: theme.white,
            border: `1.5px solid ${theme.neutral3}`,
            borderRadius: 16,
            padding: "20px 24px",
            marginBottom: 28,
          }}
        >
          <p
            style={{
              margin: "0 0 12px",
              fontSize: 13,
              fontWeight: 600,
              color: theme.neutral4,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            Kit utilisation
          </p>
          <div
            style={{
              height: 10,
              background: theme.neutral3,
              borderRadius: 99,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: "82%",
                background: theme.neutral7,
                borderRadius: 99,
                transition: "width 1s ease",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 8,
              fontSize: 11,
              color: theme.neutral4,
            }}
          >
            <span>82 installed</span>
            <span>18 available</span>
          </div>
        </div>

        {/* Section 2: Performance */}
        <SectionLabel number="2">Performance</SectionLabel>

        <div
          style={{
            display: "flex",
            gap: 14,
            marginBottom: 28,
            flexWrap: "wrap",
          }}
        >
          {/* Monthly chart */}
          <ChartCard title="Referrals & successful installs — monthly">
            <VictoryChart
              height={180}
              padding={{ top: 10, bottom: 36, left: 32, right: 10 }}
              domainPadding={{ x: 12 }}
            >
              <VictoryAxis style={chartAxisStyle} />
              <VictoryAxis dependentAxis style={chartAxisStyle} />
              <VictoryGroup offset={10}>
                <VictoryBar
                  data={referralsData}
                  x="x"
                  y="referrals"
                  style={{ data: { fill: theme.primary6, width: 8 } }}
                  labelComponent={<VictoryTooltip />}
                />
                <VictoryBar
                  data={referralsData}
                  x="x"
                  y="installs"
                  style={{ data: { fill: theme.primary4, width: 8 } }}
                  labelComponent={<VictoryTooltip />}
                />
                <VictoryBar
                  data={referralsData}
                  x="x"
                  y="unsuccessful"
                  style={{ data: { fill: theme.neutral3, width: 8 } }}
                  labelComponent={<VictoryTooltip />}
                />
              </VictoryGroup>
            </VictoryChart>
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                padding: "4px 0 8px",
              }}
            >
              {[
                { color: theme.primary6, label: "Referrals" },
                { color: theme.primary4, label: "Successful installs" },
                { color: theme.neutral3, label: "Unsuccessful" },
              ].map((l) => (
                <div
                  key={l.label}
                  style={{ display: "flex", alignItems: "center", gap: 5 }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: l.color,
                    }}
                  />
                  <span style={{ fontSize: 10, color: theme.neutral4 }}>
                    {l.label}
                  </span>
                </div>
              ))}
            </div>
          </ChartCard>

          {/* Cumulative chart */}
          <ChartCard title="Referrals & successful installs — cumulative">
            <VictoryChart
              height={180}
              padding={{ top: 10, bottom: 36, left: 36, right: 10 }}
              domainPadding={{ x: 20 }}
            >
              <VictoryAxis style={chartAxisStyle} />
              <VictoryAxis dependentAxis style={chartAxisStyle} />
              <VictoryGroup offset={14}>
                <VictoryBar
                  data={cumulativeData}
                  x="x"
                  y="referrals"
                  style={{ data: { fill: theme.primary6, width: 12 } }}
                />
                <VictoryBar
                  data={cumulativeData}
                  x="x"
                  y="installs"
                  style={{ data: { fill: theme.primary4, width: 12 } }}
                />
              </VictoryGroup>
            </VictoryChart>
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                padding: "4px 0 8px",
              }}
            >
              {[
                { color: theme.primary6, label: "Referrals" },
                { color: theme.primary4, label: "Successful installs" },
              ].map((l) => (
                <div
                  key={l.label}
                  style={{ display: "flex", alignItems: "center", gap: 5 }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: l.color,
                    }}
                  />
                  <span style={{ fontSize: 10, color: theme.neutral4 }}>
                    {l.label}
                  </span>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>

        {/* Section 3: All-time performance */}
        <SectionLabel number="3">All-time performance</SectionLabel>

        <div
          style={{
            display: "flex",
            gap: 14,
            marginBottom: 28,
            flexWrap: "wrap",
          }}
        >
          <StatCard
            label="Total referrals to date"
            value="284"
            sub="Residents referred to UBI across the lifetime of the contract."
          />
          <StatCard
            label="Successful installs to date"
            value="225"
            sub="225 successful installs against 100 available kits."
          />
        </div>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: theme.neutral3,
            marginBottom: 28,
          }}
        />

        {/* Engagement stats */}
        <div
          style={{
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          {/* Friends logged in */}
          <div
            style={{
              background: theme.white,
              border: `1.5px solid ${theme.neutral3}`,
              borderRadius: 16,
              padding: "24px 28px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: theme.neutral4,
                lineHeight: 1.4,
              }}
            >
              Friend or family members that have{" "}
              <span style={{ fontWeight: 700, color: theme.neutral7 }}>
                logged in this week
              </span>
            </p>
            <p
              style={{
                margin: "10px 0 0",
                fontSize: 40,
                fontWeight: 700,
                fontFamily: "'DM Serif Display', serif",
                color: theme.neutral7,
                lineHeight: 1.1,
              }}
            >
              30/47
            </p>
            {/* Mini progress */}
            <div
              style={{
                height: 6,
                background: theme.neutral3,
                borderRadius: 99,
                overflow: "hidden",
                margin: "12px 0 8px",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${(30 / 47) * 100}%`,
                  background: theme.expected2,
                  borderRadius: 99,
                }}
              />
            </div>
            <p style={{ margin: 0, fontSize: 12, color: theme.neutral4 }}>
              30 of 47 friends or family have logged in at least once this week,
              representing 63% of those invited.
            </p>
          </div>

          {/* Residents with invite */}
          <div
            style={{
              background: theme.white,
              border: `1.5px solid ${theme.neutral3}`,
              borderRadius: 16,
              padding: "24px 28px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: theme.neutral4,
                lineHeight: 1.4,
              }}
            >
              Residents with at least one friend or family member{" "}
              <span style={{ fontWeight: 700, color: theme.neutral7 }}>
                invited
              </span>
            </p>
            <p
              style={{
                margin: "10px 0 0",
                fontSize: 40,
                fontWeight: 700,
                fontFamily: "'DM Serif Display', serif",
                color: theme.neutral7,
                lineHeight: 1.1,
              }}
            >
              47/82{" "}
              <span
                style={{
                  fontSize: 24,
                  fontWeight: 400,
                  color: theme.neutral4,
                }}
              >
                (57%)
              </span>
            </p>
            {/* Mini progress */}
            <div
              style={{
                height: 6,
                background: theme.neutral3,
                borderRadius: 99,
                overflow: "hidden",
                margin: "12px 0 8px",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: "57%",
                  background: theme.neutral7,
                  borderRadius: 99,
                }}
              />
            </div>
            <p style={{ margin: 0, fontSize: 12, color: theme.neutral4 }}>
              47 of 82 residents have a friend or family member invited to use
              the App.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
